import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { ImportApiResult } from '../interfaces/Import/ShipmentBulkUploadApi'

type DjangoAuthData = {
  access_token: string
  expires_in: number
  token_type: 'Bearer' | string
  scope: string
  refresh_token: string
}

type DjangoAuthOkResponse = {
  code: string
  error: false
  message: string
  data: DjangoAuthData
  error_data: Record<string, unknown>
}

type DjangoAuthErrorResponse = {
  code: string
  error: true
  message: string
  data: null
  error_data: { error?: string } | Record<string, unknown>
}

type DjangoAuthResponse = DjangoAuthOkResponse | DjangoAuthErrorResponse

const {
  REACT_APP_URL_PATH_API_DJANGO,
  REACT_APP_CLIENT_ID_API_DJANGO,
  REACT_APP_CLIENT_SECRET_API_DJANGO,
} = process.env as Record<string, string | undefined>

const STORAGE_KEY = 'django_oauth'

type StoredAuth = DjangoAuthData & { expires_at?: number }

function getStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as StoredAuth
  } catch {
    return null
  }
}

function isExpired(a: StoredAuth | null): boolean {
  return !a?.access_token || !a.expires_at || Date.now() >= a.expires_at
}

export const apiDjango: AxiosInstance = axios.create({
  baseURL: REACT_APP_URL_PATH_API_DJANGO,
  timeout: 30000,
})

// El backend Django localiza sus respuestas (mensajes y errores) según ?lang=<código>.
// Lo inyectamos en TODA petición para recibir el texto ya en español y no traducir en
// el front. Se mergea con los params existentes; va como default, así un caller podría
// sobrescribir `lang` puntualmente si alguna vez hiciera falta otro idioma.
apiDjango.interceptors.request.use((config) => {
  // `params` puede venir como objeto plano, URLSearchParams o string. El spread solo es
  // seguro para objetos planos (spread-ear un URLSearchParams/string descarta o corrompe
  // los params existentes). `lang=es` va como default y nunca pisa un `lang` ya puesto
  // por el caller.
  // OAuth2: el endpoint de token NO admite query params (oauthlib responde
  // "URL query parameters are not allowed"). Por eso NO inyectamos lang=es ahí.
  const isTokenEndpoint = (config.url || '').includes('auth/get-token')
  if (!isTokenEndpoint) {
    const params = config.params
    if (params instanceof URLSearchParams) {
      if (!params.has('lang')) params.set('lang', 'es')
    } else if (typeof params === 'string') {
      if (!/(^|&)lang=/.test(params))
        config.params = params ? `${params}&lang=es` : 'lang=es'
    } else {
      config.params = { lang: 'es', ...(params || {}) }
    }
  }

  // Re-aplica el token desde localStorage en CADA petición. Si la vista se recarga o el
  // header por defecto se perdió, igual viaja la autenticación (mientras el token no esté
  // vencido). Así evitamos el "Falta el encabezado de autenticación" por header ausente.
  // La expiración la maneja el response interceptor (401 → refresh → reintento).
  const hasAuthHeader = !!(config.headers as any)?.Authorization
  if (!hasAuthHeader) {
    const auth = getStoredAuth()
    if (!isExpired(auth)) {
      config.headers = config.headers || ({} as any)
      ;(config.headers as any).Authorization = `${
        auth!.token_type || 'Bearer'
      } ${auth!.access_token}`
    }
  }
  return config
})

// Refresh del access_token usando el refresh_token guardado (grant OAuth2 estándar sobre
// el mismo endpoint /auth/get-token). Deduplicado: varias peticiones que fallan en
// paralelo comparten un único refresh en vuelo. Usa axios "crudo" (no apiDjango) para no
// recursar en los interceptores.
let refreshPromise: Promise<boolean> | null = null

export async function refreshDjangoToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const auth = getStoredAuth()
      if (
        !auth?.refresh_token ||
        !REACT_APP_CLIENT_ID_API_DJANGO ||
        !REACT_APP_CLIENT_SECRET_API_DJANGO
      ) {
        return false
      }
      const body = new URLSearchParams()
      body.append('grant_type', 'refresh_token')
      body.append('refresh_token', auth.refresh_token)
      const basicAuth =
        'Basic ' +
        btoa(
          `${REACT_APP_CLIENT_ID_API_DJANGO}:${REACT_APP_CLIENT_SECRET_API_DJANGO}`
        )
      const resp = await axios.post<DjangoAuthResponse>(
        `${REACT_APP_URL_PATH_API_DJANGO}/auth/get-token`,
        body.toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: basicAuth,
          },
          // Sin query params: el endpoint OAuth los rechaza ("URL query parameters
          // are not allowed").
          timeout: 30000,
        }
      )
      if (
        resp.status === 200 &&
        (resp.data as DjangoAuthOkResponse)?.error === false
      ) {
        setAuth((resp.data as DjangoAuthOkResponse).data)
        return true
      }
      clearDjangoAuth()
      return false
    } catch {
      clearDjangoAuth()
      return false
    }
  })()
  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

// 401 → intenta refrescar el token una vez y reintenta la petición original.
apiDjango.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error?.config
    const status = error?.response?.status
    if (status === 401 && original && !original._retry) {
      original._retry = true
      const ok = await refreshDjangoToken()
      if (ok) {
        const auth = getStoredAuth()
        if (auth?.access_token) {
          original.headers = original.headers || {}
          original.headers.Authorization = `${
            auth.token_type || 'Bearer'
          } ${auth.access_token}`
        }
        return apiDjango(original)
      }
    }
    return Promise.reject(error)
  }
)

function setAuth(data: DjangoAuthData) {
  const expiresAt = Date.now() + data.expires_in * 1000
  const saved = { ...data, expires_at: expiresAt }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved))
  } catch {}
  apiDjango.defaults.headers.common.Authorization = `${data.token_type} ${data.access_token}`
}

export function clearDjangoAuth() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {}
  delete apiDjango.defaults.headers.common.Authorization
}

export function restoreDjangoAuthFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as DjangoAuthData & { expires_at?: number }
    if (!parsed.access_token || !parsed.expires_at) return
    if (Date.now() >= parsed.expires_at) {
      // Vencido al recargar la vista: NO borramos el storage (perderíamos el
      // refresh_token). Intentamos refrescar en segundo plano para volver a rellenar y
      // guardar el token; el response interceptor (401) también lo cubre si esto falla.
      if (parsed.refresh_token) void refreshDjangoToken()
      return
    }
    apiDjango.defaults.headers.common.Authorization = `${parsed.token_type} ${parsed.access_token}`
  } catch {
    clearDjangoAuth()
  }
}

export async function loginDjango(
  username: string,
  password: string
): Promise<DjangoAuthData> {
  if (!REACT_APP_CLIENT_ID_API_DJANGO || !REACT_APP_CLIENT_SECRET_API_DJANGO) {
    throw new Error('Faltan CLIENT_ID o CLIENT_SECRET en .env')
  }

  const body = new URLSearchParams()
  body.append('grant_type', 'password')
  body.append('username', username)
  body.append('password', password)

  const basicAuth =
    'Basic ' +
    btoa(
      `${REACT_APP_CLIENT_ID_API_DJANGO}:${REACT_APP_CLIENT_SECRET_API_DJANGO}`
    )

  const resp = await apiDjango.post<DjangoAuthResponse>(
    '/auth/get-token',
    body.toString(),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: basicAuth,
      },
    }
  )

  if (
    resp.status === 200 &&
    resp.data &&
    (resp.data as DjangoAuthOkResponse).error === false
  ) {
    const data = (resp.data as DjangoAuthOkResponse).data as DjangoAuthData
    setAuth(data)
    return data
  }

  const message = String(
    (resp.data as any)?.message ??
      (resp.data as any)?.error_data?.error ??
      'Error autenticando contra API Django'
  )
  throw new Error(message)
}

export function hasValidDjangoToken(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw) as { expires_at?: number }
    return !!parsed.expires_at && Date.now() < (parsed.expires_at as number)
  } catch {
    return false
  }
}

/**
 * Desenvuelve el envelope estándar { code, error, message, data, error_data }.
 * REGLA (spec §6.2): NUNCA ramificar por status HTTP; siempre mirar `error`/`code` del body.
 * El backend responde 200 incluso en errores de negocio; el catch cubre red caída y, de
 * forma defensiva, un 4xx/5xx con envelope en e.response.data.
 */
export async function unwrap<T>(
  p: Promise<AxiosResponse<any>>
): Promise<ImportApiResult<T>> {
  try {
    const resp = await p
    const body = (resp?.data ?? {}) as any
    const isError = body.error === true
    return {
      didError: isError,
      code: body.code ?? (isError ? 'unknown' : '0'),
      message: body.message ?? (isError ? 'Error' : 'OK'),
      errorMessage: isError ? (body.message ?? 'Error') : '',
      model: (body.data ?? null) as T | null,
      errorData: body.error_data ?? {},
    }
  } catch (e: any) {
    const body = e?.response?.data
    if (body && typeof body === 'object' && 'error' in body) {
      return {
        didError: true,
        code: body.code ?? 'unknown',
        message: body.message ?? 'Error',
        errorMessage: body.message ?? 'Error',
        model: null,
        errorData: body.error_data ?? {},
      }
    }
    const msg = e?.message || 'Error de red'
    return {
      didError: true,
      code: 'network',
      message: msg,
      errorMessage: msg,
      model: null,
      errorData: {},
      httpStatus: e?.response?.status,
    }
  }
}
